import React, { useState } from 'react';
import { LayoutDashboard, Users, Calendar, CheckSquare, BarChart3, FileText, Menu, X, Heart, ExternalLink, Sun, Moon } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const Layout = ({ children, activeTab, setActiveTab }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { theme, toggleTheme, isDark } = useTheme();

  const navigation = [
    { id: 'dashboard', name: 'Dashboard', icon: LayoutDashboard },
    { id: 'employees', name: 'Employees', icon: Users },
    { id: 'attendance', name: 'Attendance', icon: Calendar },
    { id: 'tasks', name: 'Tasks', icon: CheckSquare },
    { id: 'calendar', name: 'Calendar', icon: BarChart3 },
    { id: 'reports', name: 'Reports', icon: FileText },
  ];

  const handleNavClick = (tabId) => {
    setActiveTab(tabId);
    setSidebarOpen(false); // Close mobile sidebar on navigation
  };

  return (
    <div className={`min-h-screen transition-all duration-500 ${
      isDark 
        ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-slate-900' 
        : 'bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50'
    }`}>
      {/* Animated Background Particles */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-20 left-10 w-64 h-64 rounded-full blur-3xl opacity-20 animate-float ${
          isDark ? 'bg-purple-500' : 'bg-blue-400'
        }`} style={{ animationDuration: '8s' }}></div>
        <div className={`absolute bottom-20 right-10 w-96 h-96 rounded-full blur-3xl opacity-20 animate-float ${
          isDark ? 'bg-indigo-500' : 'bg-indigo-400'
        }`} style={{ animationDuration: '10s', animationDelay: '2s' }}></div>
        <div className={`absolute top-1/2 left-1/2 w-72 h-72 rounded-full blur-3xl opacity-10 animate-float ${
          isDark ? 'bg-pink-500' : 'bg-purple-400'
        }`} style={{ animationDuration: '12s', animationDelay: '4s' }}></div>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-[65] bg-black bg-opacity-50 backdrop-blur-sm lg:hidden animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-[70] w-64 backdrop-blur-xl shadow-2xl transform transition-all duration-300 ease-in-out lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } ${
        isDark 
          ? 'bg-gray-900/95 border-r border-gray-700/50' 
          : 'bg-white/80 border-r border-white/20'
      }`}>
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className={`flex h-16 shrink-0 items-center justify-between px-6 border-b ${isDark ? 'border-gray-700/50' : 'border-white/20'}`}>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center shadow-lg">
                <LayoutDashboard className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                JR Employee Tracker
              </h1>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className={`lg:hidden p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
            >
              <X className={`w-5 h-5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} />
            </button>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 space-y-2 px-4 py-6">
            {navigation.map((item, index) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`group flex w-full items-center rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200 transform hover:scale-105 animate-slide-up ${
                    activeTab === item.id
                      ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg shadow-primary-500/25'
                      : isDark
                        ? 'text-gray-300 hover:bg-gray-800/60 hover:text-white hover:shadow-md'
                        : 'text-gray-700 hover:bg-white/60 hover:text-gray-900 hover:shadow-md'
                  }`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <Icon className={`mr-3 h-5 w-5 shrink-0 transition-transform duration-200 ${
                    activeTab === item.id ? 'scale-110' : 'group-hover:scale-110'
                  }`} />
                  {item.name}
                </button>
              );
            })}
          </nav>

          {/* Theme Toggle */}
          <div className={`px-4 py-3 border-t ${isDark ? 'border-gray-700/50' : 'border-white/20'}`}>
            <button
              onClick={toggleTheme}
              className={`w-full flex items-center justify-center space-x-3 px-4 py-3 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 ${
                isDark
                  ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-lg shadow-yellow-500/25 hover:shadow-yellow-500/40'
                  : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40'
              }`}
            >
              {isDark ? (
                <>
                  <Sun className="w-5 h-5 animate-spin" style={{ animationDuration: '3s' }} />
                  <span>Light Mode</span>
                </>
              ) : (
                <>
                  <Moon className="w-5 h-5" />
                  <span>Dark Mode</span>
                </>
              )}
            </button>
          </div>

          {/* Footer */}
          <div className={`p-4 border-t ${isDark ? 'border-gray-700/50' : 'border-white/20'}`}>
            <div className={`text-xs text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              © 2025 JR Employee Tracker
            </div>
          </div>
        </div>
      </div>

      {/* Mobile header */}
      <div className={`lg:hidden fixed top-0 left-0 right-0 z-[60] backdrop-blur-xl px-4 py-3 ${isDark ? 'bg-gray-900/95 border-b border-gray-700/50' : 'bg-white/80 border-b border-white/20'}`}>
        <div className="flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
          >
            <Menu className={`w-6 h-6 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} />
          </button>
          <h1 className="text-lg font-semibold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
            JR Employee Tracker
          </h1>
          <button
            onClick={toggleTheme}
            className={`p-2 rounded-lg transition-all duration-300 ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
          >
            {isDark ? (
              <Sun className={`w-5 h-5 text-yellow-400`} />
            ) : (
              <Moon className={`w-5 h-5 text-indigo-600`} />
            )}
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 pt-16 lg:pt-0 min-h-screen flex flex-col">
        <main className="py-4 sm:py-6 lg:py-8 animate-fade-in flex-1">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="animate-slide-up">
              {children}
            </div>
          </div>
        </main>
        
        {/* Modern Animated Footer */}
        <footer className={`relative backdrop-blur-xl transition-all duration-500 ${
          isDark
            ? 'bg-gradient-to-r from-gray-900 via-slate-900 to-gray-900 border-t border-gray-700/50'
            : 'bg-gradient-to-r from-slate-50 via-blue-50 to-indigo-50 border-t border-white/20'
        }`}>
          {/* Animated background particles */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-2 left-1/4 w-2 h-2 bg-primary-400/30 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
            <div className="absolute top-4 right-1/3 w-1 h-1 bg-secondary-400/40 rounded-full animate-bounce" style={{ animationDelay: '1s' }}></div>
            <div className="absolute bottom-3 left-1/2 w-1.5 h-1.5 bg-purple-400/35 rounded-full animate-bounce" style={{ animationDelay: '2s' }}></div>
          </div>
          
          <div className="relative z-10 py-6 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                {/* Left side - Copyright */}
                <div className={`flex items-center space-x-2 text-sm animate-slide-up ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  <span>© 2025 JR Employee Tracker</span>
                  <div className={`w-1 h-1 rounded-full ${isDark ? 'bg-gray-600' : 'bg-gray-400'}`}></div>
                  <span>All rights reserved</span>
                </div>
                
                {/* Center/Right - Made by */}
                <div className="flex items-center space-x-2 group animate-slide-up" style={{ animationDelay: '200ms' }}>
                  <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Made with</span>
                  <Heart className="w-4 h-4 text-red-500 animate-pulse group-hover:scale-110 transition-transform duration-300" />
                  <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>by</span>
                  <a
                    href="https://jijanurrahman.netlify.app" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-1 px-3 py-1.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white text-sm font-semibold rounded-full hover:from-primary-600 hover:to-secondary-600 transition-all duration-300 transform hover:scale-105 hover:shadow-lg group-hover:animate-pulse"
                  >
                    <span>Jijanur Rahman</span>
                    <ExternalLink className="w-3 h-3 opacity-75 group-hover:opacity-100 transition-opacity duration-300" />
                  </a>
                </div>
              </div>
              
              {/* Animated gradient line */}
              <div className="mt-4 h-px bg-gradient-to-r from-transparent via-primary-300 to-transparent animate-pulse"></div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Layout;